import { SearchableSelect } from "@/components/ui/searchable-select";
import { ApplyFromUrl } from "@/lib/openWithConfig";
import { ListEmpty } from "@/components/patterns";
import { dateLocale, withClock } from "@/lib/dateTime";
import { CallLink } from "@/components/calling/CallLink";
import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { LeadTasksSection } from "./LeadTasksSection";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  UserPlus,
  Edit,
  Trash2,
  RefreshCw,
  Search,
  AlertTriangle,
  ArrowRightCircle,
  Building,
  Mail,
  Phone,
  UserCheck,
  Ban,
  CheckCircle,
  Clock,
  Briefcase,
  IndianRupee,
  Flame,
  Zap,
  Snowflake,
  Inbox,
  User,
  Users,
  Calendar,
  History,
  Tag,
  Globe,
  MapPin,
  HelpCircle,
  Share2,
  Upload,
  FileSpreadsheet,
  PhoneForwarded,
} from "lucide-react";
import { formatINR, cn } from "@/lib/utils";
import type { LeadRecord, LeadStatus, LeadPriority, MasterLookupRecord } from "@/types";
import { FilterBar } from "@/components/filters/FilterBar";
import { OwnerCell } from "@/components/inline/OwnerCell";
import { LeadsBoard } from "./LeadsBoard";
import { LeadMergeDialog } from "@/components/leads/LeadMergeDialog";
import { DuplicateGroupsDialog } from "@/components/leads/DuplicateGroupsDialog";
import { clearFollowUpSkipped } from "./record/FollowUpBar";
import { PreviewButton } from "@/components/preview/PreviewButton";
import { BulkEditDialog, type BulkField } from "@/components/inline/BulkEditDialog";
import { BulkBar, BulkButton, PageHeader, Person, StatusPill, StatusTabs, ViewSwitch, ViewsPanel } from "@/components/patterns";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { useFieldFilters, type FilterFieldDef } from "@/lib/useFieldFilters";
import { useSort } from "@/lib/useSort";
import { InlineCell } from "@/components/inline/InlineCell";
import { usePersistentColumns } from "@/lib/usePersistentColumns";
import { downloadCsv, fetchAllRows } from "@/lib/exportCsv";
import { CampaignDialog } from "@/components/whatsapp/CampaignDialog";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, valueOptionsFromFacets, type OwnerFacet } from "@/lib/filters";
import {
  CalendarDays as FilterDateIcon,
  Flame as FilterPriorityIcon,
  MapPin as FilterStateIcon,
  Hourglass as FilterInactiveIcon,
  Megaphone as FilterSourceIcon,
  UserCircle2 as FilterOwnerIcon,
} from "lucide-react";

// Remembered per browser; "all" / "any" mean no filter
const LEAD_COLUMNS = [
  { key: "prospect", label: "Prospect & contact", locked: true },
  { key: "company", label: "Company & location" },
  { key: "owner", label: "Owner" },
  { key: "need", label: "Requirement · source" },
  { key: "priority", label: "Priority" },
  { key: "followup", label: "Follow-up" },
  { key: "actions", label: "Actions", locked: true },
];
const LEAD_COLUMN_KEYS = LEAD_COLUMNS.map((c) => c.key);
const LEAD_FILTER_DEFAULTS = { owner: "all", priority: "all", source: "all", state: "all", created: "any", inactive: "any" };
/** Open leads nobody has contacted for at least this long (for any other number: + Add filter → Days since last activity) */
const INACTIVE_OPTIONS = [
  { value: "any", label: "Any time" },
  ...[3, 7, 15, 30, 60, 90].map((d) => ({ value: String(d), label: `${d}+ days` })),
];
const inactiveParam = (v?: string) => (!v || v === "any" ? "" : v);
const PRIORITY_OPTIONS = [
  { value: "all", label: "All priorities" },
  { value: "HOT", label: "Hot" },
  { value: "WARM", label: "Warm" },
  { value: "COLD", label: "Cold" },
];

const LEAD_VIEWS = ["all", "my", "team", "pool", "hot", "followup", "followup_today", "contacted_today", "untouched", "new_week", "converted"];
const viewFromUrl = (search: string) => {
  const t = new URLSearchParams(search).get("tab");
  return t && LEAD_VIEWS.includes(t) ? t : "all";
};

const STATUS_CONFIG: Record<
  LeadStatus,
  { label: string; bg: string; text: string }
> = {
  NEW: { label: "New", bg: "bg-primary/10 border-primary/20", text: "text-primary" },
  CONTACTED: { label: "Contacted", bg: "bg-warning/10 border-warning/20", text: "text-warning" },
  FOLLOW_UP: { label: "Follow-up", bg: "bg-info/10 border-info/20", text: "text-info" },
  QUALIFIED: { label: "Qualified", bg: "bg-primary/10 border-primary/20", text: "text-accent-foreground" },
  CONVERTED: { label: "Converted", bg: "bg-success/10 border-success/20", text: "text-success" },
  DISQUALIFIED: { label: "Disqualified", bg: "bg-destructive/10 border-destructive/20", text: "text-destructive" },
};

const LEAD_BULK_FIELDS: BulkField[] = [
  { field: "city", label: "City", kind: "text", nullable: true },
  { field: "state", label: "State", kind: "text", nullable: true },
  { field: "region", label: "Region", kind: "text", nullable: true },
  { field: "source", label: "Source", kind: "text" },
  { field: "industry", label: "Industry", kind: "text", nullable: true },
  { field: "company_size", label: "Company size", kind: "text", nullable: true },
  { field: "expected_budget", label: "Expected budget", kind: "number" },
  { field: "next_follow_up_at", label: "Next follow-up", kind: "datetime", nullable: true },
];

export function LeadsPage() {
  const { hasPermission, user, getScope } = useAuth();
  // Sales Executives (scope "own") have no team, so the Team tab is hidden for them
  const canSeeTeam = getScope("lead") !== "own";
  const { success, error } = useToast();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const columns = usePersistentColumns("leads", LEAD_COLUMN_KEYS);
  const colHidden = (k: string) => (columns.has(k) ? "" : "hidden");
  // A link such as /leads?tab=untouched (the top bar, an alert) opens that view
  const { search: urlSearch } = useLocation();
  const [view, setView] = useState(() => viewFromUrl(window.location.search));
  useEffect(() => {
    const v = viewFromUrl(urlSearch);
    if (v !== "all") {
      setView(v);
      setSelectedLeadIds(new Set());
    }
  }, [urlSearch]);
  const [statusTab, setStatusTab] = useState("all");
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({});
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  // From the follow-up list, a lead page offers Next, to go through the list one by one
  const followUpLinks = view === "followup" || statusTab === "FOLLOW_UP" ? "?followup=1" : "";
  const startFollowUps = async () => {
    clearFollowUpSkipped();
    try {
      const r = unwrapObject<{ left: number; next_id: string | null }>(await api.get("/api/Lead/FollowUpNext"));
      if (r.next_id) navigate(`/leads/${r.next_id}?followup=1`);
      else success("Nothing to follow up", "Every lead in the list has been contacted");
    } catch (e: any) {
      error("Couldn't open the list", e.message || "Please try again");
    }
  };
  const [topSources, setTopSources] = useState<{ value: string; count: number }[]>([]);
  // Bulk actions beyond reassign
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [mergeIds, setMergeIds] = useState<[string, string] | null>(null);
  const [dupesOpen, setDupesOpen] = useState(false);
  const [viewMode, setViewModeState] = useState<"table" | "board">(() => {
    try {
      return localStorage.getItem("leads.layout") === "board" ? "board" : "table";
    } catch {
      return "table";
    }
  });
  const setViewMode = (m: "table" | "board") => {
    setViewModeState(m);
    try {
      localStorage.setItem("leads.layout", m);
    } catch {
      /* the choice just isn't remembered */
    }
  };
  const [bulkPriority, setBulkPriority] = useState("HOT");
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", due: "", type: "CALL" });
  const [deleteManyOpen, setDeleteManyOpen] = useState(false);
  const leadFilters = usePersistentFilters("leads", LEAD_FILTER_DEFAULTS);
  // "+ Add filter" on any lead field the API offers
  const fieldFilters = useFieldFilters("leads");
  const sorting = useSort("leads");
  const [leadFields, setLeadFields] = useState<FilterFieldDef[] | null>(null);
  useEffect(() => {
    api.get("/api/Lead/FilterFields").then((res) => setLeadFields(unwrapArray<FilterFieldDef>(res))).catch(() => setLeadFields([]));
  }, []);
  // Filter choices with counts (from the API, within the user's scope)
  const [leadOptions, setLeadOptions] = useState<{
    total: number;
    owners: OwnerFacet[];
    sources: { value: string; count: number }[];
    states: { value: string; count: number }[];
  }>({ total: 0, owners: [], sources: [], states: [] });
  const [actionLoading, setActionLoading] = useState(false);

  // Selection for bulk actions
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());

  // Assignable Reps
  const [assignableReps, setAssignableReps] = useState<Array<{ id: string; first_name: string; last_name?: string; email: string; user_role?: { name: string } }>>([]);

  // Modals
  const [statusLead, setStatusLead] = useState<LeadRecord | null>(null);
  const [deleteLead, setDeleteLead] = useState<LeadRecord | null>(null);
  const [convertLead, setConvertLead] = useState<LeadRecord | null>(null);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [viewLead, setViewLead] = useState<LeadRecord | null>(null);
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);

  // Convert Form
  const [convertForm, setConvertForm] = useState({
    create_deal: true,
    deal_name: "",
    deal_amount: 0,
    expected_close_date: "",
  });

  // Bulk Assign Form
  const [bulkTargetOwner, setBulkTargetOwner] = useState("");
  const [bulkReason, setBulkReason] = useState("BULK_REASSIGN");

  // Single Lead Reassign
  const [reassignLead, setReassignLead] = useState<LeadRecord | null>(null);
  const [reassignOwner, setReassignOwner] = useState("");
  const [reassignReason, setReassignReason] = useState("");

  // Status Change State
  const [targetStatus, setTargetStatus] = useState<LeadStatus>("CONTACTED");
  const [followUpAt, setFollowUpAt] = useState<string>("");
  const [disqualifyReason, setDisqualifyReason] = useState<string>("");
  const [disqualifyNotes, setDisqualifyNotes] = useState("");

  const [masterSources, setMasterSources] = useState<MasterLookupRecord[]>([]);
  const [masterDisqualifyReasons, setMasterDisqualifyReasons] = useState<MasterLookupRecord[]>([]);
  const [masterIndustries, setMasterIndustries] = useState<MasterLookupRecord[]>([]);

  const canAssign =
    hasPermission("lead.assign") ||
    user?.UserRole?.RoleCode === "SUPER_ADMIN" ||
    user?.UserRole?.RoleCode === "SALES_HEAD" ||
    (user as any)?.user_role?.code === "SUPER_ADMIN" ||
    (user as any)?.user_role?.code === "SALES_HEAD" ||
    (user as any)?.role === "SUPER_ADMIN" ||
    (user as any)?.role === "SALES_HEAD" ||
    (user as any)?.isSuperAdmin === true;
  const canDelete = hasPermission("lead.delete");
  const canConvert = hasPermission("lead.convert");
  const canImport = hasPermission("lead.import");
  const canEditInline = hasPermission("lead.update");

  const list = usePagedList<LeadRecord>({
    deps: [leadFilters.values, fieldFilters.values, leadFields, search, view, statusTab, sorting.sort],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const f = leadFilters.values;
      const query = toQuery({
        search: search.trim(),
        tab: view === "all" ? "" : view,
        status: statusTab === "all" ? "" : statusTab,
        owner: f.owner,
        priority: f.priority,
        source: f.source,
        state: f.state,
        inactive_days: inactiveParam(f.inactive),
        ...dateRangeParams(f.created, "created"),
        f: fieldFilters.param(leadFields), sort: sorting.sort,
        page,
        page_size: 30,
      });
      try {
        const [res, options] = await Promise.all([
          api.get(`/api/Lead/List${query}`),
          api.get("/api/Lead/FilterOptions").catch(() => null),
        ]);
        if (options) setLeadOptions(unwrapObject(options));
        return unwrapPage<LeadRecord>(res);
      } catch (err: any) {
        throw err;
      }
    },
  });
  // The list's current filters, exactly as the list asks the server for them (a campaign can take every matching lead)
  const campaignFilters = () => {
    const f = leadFilters.values;
    return {
      search: search.trim(), tab: view === "all" ? "" : view, status: statusTab === "all" ? "" : statusTab,
      owner: f.owner, priority: f.priority, source: f.source, state: f.state, inactive_days: inactiveParam(f.inactive),
      ...dateRangeParams(f.created, "created"), f: fieldFilters.param(leadFields), sort: sorting.sort,
    };
  };
  const leads = list.rows;
  const loading = list.loading;
  const loadLeads = list.reload;

  // Counts beside each view, on each status tab, and the top sources (one request)
  useEffect(() => {
    api
      .get(`/api/Lead/ViewCounts${toQuery({ tab: view === "all" ? "" : view })}`)
      .then((r) => {
        const d = unwrapObject<{ tabs: Record<string, number>; statuses: Record<string, number>; sources: { value: string; count: number }[] }>(r);
        setViewCounts(d.tabs || {});
        setStatusCounts(d.statuses || {});
        setTopSources(d.sources || []);
      })
      .catch(() => undefined);
  }, [list.total, view]);

  const loadAssignableReps = async () => {
    try {
      const res = await api.get("/api/User/Assignable?for=lead");
      setAssignableReps(unwrapArray(res));
    } catch {
      // Non-fatal
    }
  };

  useEffect(() => {
    loadAssignableReps();
    api
      .get("/api/MasterLookup/ByCategory/LEAD_SOURCE")
      .then((res) => setMasterSources(unwrapArray<MasterLookupRecord>(res)))
      .catch(() => {});
    api
      .get("/api/MasterLookup/ByCategory/LEAD_DISQUALIFY_REASON")
      .then((res) => setMasterDisqualifyReasons(unwrapArray<MasterLookupRecord>(res)))
      .catch(() => {});
    api
      .get("/api/MasterLookup/ByCategory/INDUSTRY")
      .then((res) => setMasterIndustries(unwrapArray<MasterLookupRecord>(res)))
      .catch(() => {});
  }, []);

  const handleClaimLead = async (leadId: string) => {
    setActionLoading(true);
    try {
      await api.post(`/api/Lead/Claim/${leadId}`);
      success("Lead Claimed", "The lead is now yours");
      await loadLeads();
    } catch (err: any) {
      error("Claim Failed", err.message || "Could not claim lead");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkAssignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkTargetOwner) {
      error("Select Assignee", "Please select a sales representative");
      return;
    }

    setActionLoading(true);
    try {
      const res = await api.post("/api/Lead/BulkAssign", {
        lead_ids: Array.from(selectedLeadIds),
        target_owner_id: bulkTargetOwner,
        reason: bulkReason || "BULK_REASSIGN",
      });
      const result = res || {};
      const count = Number(result.count ?? selectedLeadIds.size);
      const skipped = Number(result.skipped ?? 0);
      success(
        "Leads Reassigned",
        `Reassigned ${count} lead${count === 1 ? "" : "s"}` +
          (skipped ? `; ${skipped} skipped (converted or outside your team)` : ""),
      );
      setBulkAssignOpen(false);
      setSelectedLeadIds(new Set());
      await loadLeads();
    } catch (err: any) {
      error("Bulk Assignment Failed", err.message || "Failed to reassign selected leads");
    } finally {
      setActionLoading(false);
    }
  };

  const openReassign = (l: LeadRecord) => {
    setReassignLead(l);
    setReassignOwner("");
    setReassignReason("");
  };

  const handleReassignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reassignLead) return;
    if (!reassignOwner) {
      error("Select Assignee", "Please select a sales representative");
      return;
    }

    setActionLoading(true);
    try {
      await api.patch(`/api/Lead/Reassign/${reassignLead.id}`, {
        owner_id: reassignOwner,
        reason: reassignReason.trim() || undefined,
      });
      success("Lead Reassigned", `${reassignLead.first_name} ${reassignLead.last_name || ""} has a new owner`);
      setReassignLead(null);
      await loadLeads();
    } catch (err: any) {
      error("Reassignment Failed", err.message || "Failed to reassign lead");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!statusLead) return;

    if (targetStatus === "DISQUALIFIED" && !disqualifyReason.trim()) {
      error("Reason Required", "Please provide a disqualification reason");
      return;
    }

    setActionLoading(true);
    try {
      await api.patch(`/api/Lead/ChangeStatus/${statusLead.id}`, {
        status: targetStatus,
        next_follow_up_at: targetStatus === "FOLLOW_UP" && followUpAt ? new Date(followUpAt).toISOString() : undefined,
        disqualify_reason: targetStatus === "DISQUALIFIED" ? disqualifyReason : undefined,
        disqualify_notes: targetStatus === "DISQUALIFIED" ? disqualifyNotes : undefined,
      });
      success("Status Updated", `Lead transitioned to ${targetStatus}`);
      setStatusLead(null);
      await loadLeads();
    } catch (err: any) {
      error("Transition Failed", err.message || "Invalid status transition");
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!convertLead) return;

    setActionLoading(true);
    try {
      const res = await api.post(`/api/Convert/Lead/${convertLead.id}`, {
        create_deal: convertForm.create_deal,
        deal_name: convertForm.deal_name.trim() || undefined,
        deal_amount: Number(convertForm.deal_amount) || undefined,
        expected_close_date: convertForm.expected_close_date ? new Date(convertForm.expected_close_date).toISOString() : undefined,
      });

      const data = res?.data || res;
      success(
        "Lead Converted Successfully",
        `Created Account: "${data.account_name || convertLead.company_name}" and Contact.`
      );
      setConvertLead(null);
      await loadLeads();
    } catch (err: any) {
      error("Conversion Failed", err.message || "Could not convert lead");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteLead) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/Lead/Delete/${deleteLead.id}`);
      success("Lead Deleted", "Lead record has been removed");
      setDeleteLead(null);
      await loadLeads();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete lead");
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = async (l: LeadRecord) => {
    setViewDetailsLoading(true);
    setViewLead(l);
    try {
      const res = await api.get(`/api/Lead/ById/${l.id}`);
      const detailed = res?.data || res;
      setViewLead(detailed);
    } catch {
      // Fallback to row data
    } finally {
      setViewDetailsLoading(false);
    }
  };

  const toggleSelectLead = (id: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeadIds.size === filteredLeads.length && filteredLeads.length > 0) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(filteredLeads.map((l) => l.id)));
    }
  };

  // The API already applied every filter
  const filteredLeads = leads;

  const selectedLeads = () => leads.filter((l) => selectedLeadIds.has(l.id));

  const LEAD_EXPORT_COLUMNS: [string, (l: LeadRecord) => string][] = [
    ["Name", (l) => `${l.first_name} ${l.last_name || ""}`.trim()],
    ["Company", (l) => l.company_name || ""],
    ["Email", (l) => l.email || ""],
    ["Phone", (l) => l.mobile || l.phone || ""],
    ["City", (l) => l.city || ""],
    ["State", (l) => l.state || ""],
    ["Source", (l) => l.source || ""],
    ["Requirement", (l) => l.requirement_notes || l.product_interest || ""],
    ["Priority", (l) => l.priority || ""],
    ["Score", (l) => String(l.score ?? "")],
    ["Status", (l) => l.lead_status || ""],
    ["Owner", (l) => (l.owner ? `${l.owner.first_name} ${l.owner.last_name || ""}`.trim() : "")],
    ["Next follow-up", (l) => (l.next_follow_up_at ? new Date(l.next_follow_up_at).toLocaleString(dateLocale(), withClock()) : "")],
  ];
  const fileName = () => `leads-${new Date().toISOString().slice(0, 10)}.csv`;

  const exportSelected = () => downloadCsv(fileName(), LEAD_EXPORT_COLUMNS, selectedLeads());

  // Everything the current view, status tab, filters and search show (up to the export limit)
  const [exporting, setExporting] = useState(false);
  const exportAll = async () => {
    setExporting(true);
    try {
      const f = leadFilters.values;
      const query = toQuery({
        search: search.trim(),
        tab: view === "all" ? "" : view,
        status: statusTab === "all" ? "" : statusTab,
        owner: f.owner,
        priority: f.priority,
        source: f.source,
        state: f.state,
        inactive_days: inactiveParam(f.inactive),
        ...dateRangeParams(f.created, "created"),
        f: fieldFilters.param(leadFields), sort: sorting.sort,
      });
      const { rows, total } = await fetchAllRows<LeadRecord>("/api/Lead/List", query);
      downloadCsv(fileName(), LEAD_EXPORT_COLUMNS, rows);
      if (total > rows.length) toastWarn(rows.length, total);
    } catch (e: any) {
      error("Could not export", e?.message || "Try again");
    } finally {
      setExporting(false);
    }
  };
  const toastWarn = (shown: number, total: number) => error("Export is partial", `The first ${shown} of ${total} leads were saved. Narrow the filters to get the rest.`);

  const submitBulkPriority = async () => {
    setActionLoading(true);
    try {
      const r: any = await api.post("/api/Lead/BulkPriority", { lead_ids: Array.from(selectedLeadIds), priority: bulkPriority });
      success("Priority changed", `${r?.count ?? 0} lead${r?.count === 1 ? "" : "s"} set to ${bulkPriority.toLowerCase()}${r?.skipped ? `; ${r.skipped} skipped (converted or outside your team)` : ""}`);
      setPriorityOpen(false);
      setSelectedLeadIds(new Set());
      await loadLeads();
    } catch (err: any) {
      error("Could not change priority", err.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  const submitBulkTask = async () => {
    if (!taskForm.title.trim() || !taskForm.due) return;
    setActionLoading(true);
    let made = 0;
    try {
      for (const l of selectedLeads()) {
        await api.post("/api/Task/Insert", {
          title: taskForm.title.trim(),
          task_type: taskForm.type,
          priority: "NORMAL",
          due_at: new Date(taskForm.due).toISOString(),
          assigned_to_id: l.owner_id || user?.Id,
          related_type: "LEAD",
          related_id: l.id,
          related_name: `${l.first_name} ${l.last_name || ""}`.trim(),
        });
        made++;
      }
      success("Tasks added", `${made} task${made === 1 ? "" : "s"} created`);
      setTaskOpen(false);
      setSelectedLeadIds(new Set());
      await loadLeads();
    } catch (err: any) {
      error(`Added ${made}, then stopped`, err.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  const submitBulkDelete = async () => {
    setActionLoading(true);
    try {
      const r: any = await api.post("/api/Lead/BulkDelete", { lead_ids: Array.from(selectedLeadIds) });
      success("Leads deleted", `${r?.count ?? 0} deleted${r?.kept?.length ? `; ${r.kept.length} kept (converted or outside your team)` : ""}`);
      setDeleteManyOpen(false);
      setSelectedLeadIds(new Set());
      await loadLeads();
    } catch (err: any) {
      error("Could not delete", err.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  const renderPriorityBadge = (priority?: LeadPriority, score?: number) => {
    const p = priority || (score && score >= 60 ? "HOT" : score && score < 30 ? "COLD" : "WARM");
    if (p === "HOT") return <StatusPill tone="danger"><Flame className="size-3" />Hot ({score || 70})</StatusPill>;
    if (p === "COLD") return <StatusPill tone="neutral"><Snowflake className="size-3" />Cold ({score || 20})</StatusPill>;
    return <StatusPill tone="warning"><Zap className="size-3" />Warm ({score || 45})</StatusPill>;
  };

  const renderFollowUpPill = (followUpDate?: string) => {
    if (!followUpDate) return <span className="text-xs text-muted-foreground italic">None scheduled</span>;
    const target = new Date(followUpDate);
    const now = new Date();
    const isPast = target.getTime() < now.getTime() - 1000 * 60 * 60; // 1 hour grace
    const isToday = target.toDateString() === now.toDateString();

    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border",
          isPast
            ? "bg-danger/10 text-danger border-danger/20"
            : isToday
            ? "bg-warning/10 text-warning border-warning/20"
            : "bg-muted/50 text-foreground border-border"
        )}
      >
        <Calendar className="size-3 shrink-0" />
        <span>
          {isPast ? "Overdue: " : isToday ? "Today: " : ""}
          {target.toLocaleDateString(dateLocale(), { month: "short", day: "numeric" })}
        </span>
      </span>
    );
  };

  // A saved view, or the filters Ask the CRM hands over with ?apply=
  const applySaved = (c: any) => {
    setViewMode(c.layout === "board" ? "board" : "table");
    setView(c.view || "all");
    setStatusTab(c.statusTab || "all");
    setSearch(c.search || "");
    leadFilters.replace(c.filters || {});
    fieldFilters.setAll(c.fieldFilters);
    sorting.setSort(c.sort);
    setSelectedLeadIds(new Set());
  };

  return (
    <div className="space-y-4">
      <ApplyFromUrl apply={applySaved} />
      <ViewsPanel
        title="Leads"
        saved={{
          module: "leads",
          getConfig: () => ({ view, statusTab, search, filters: leadFilters.values, fieldFilters: fieldFilters.values, sort: sorting.sort, layout: viewMode }),
          apply: applySaved,
        }}
        subtitle="Inquiries and prospects"
        active={view}
        onSelect={(k) => {
          setView(k);
          setSelectedLeadIds(new Set());
        }}
        groups={[
          {
            items: [
              { key: "all", label: "All leads", count: viewCounts.all },
              { key: "my", label: "My leads", count: viewCounts.my },
              { key: "team", label: "My team", count: viewCounts.team, hidden: !canSeeTeam },
              { key: "pool", label: "Unassigned pool", count: viewCounts.pool, tone: "warning" },
              { key: "hot", label: "Hot", count: viewCounts.hot, tone: "gold" },
              { key: "followup", label: "Follow-up", count: viewCounts.followup, tone: "warning" },
              { key: "followup_today", label: "Call back today", count: viewCounts.followup_today },
              { key: "contacted_today", label: "Contacted today", count: viewCounts.contacted_today },
              { key: "untouched", label: "Not contacted", count: viewCounts.untouched, tone: "danger" },
              { key: "new_week", label: "New this week", count: viewCounts.new_week },
              { key: "converted", label: "Converted", count: viewCounts.converted },
            ],
          },
        ]}
        footer={
          topSources.length > 0 ? (
            <div className="mt-3 flex flex-col gap-1.5 px-2.5">
              <div className="pb-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">Lead sources</div>
              {topSources.map((src) => (
                <div key={src.value} className="flex flex-col gap-1">
                  <div className="flex justify-between text-xs">
                    <span className="truncate text-foreground">{src.value}</span>
                    <span className="font-mono text-muted-foreground">{src.count}</span>
                  </div>
                  <div className="h-1 rounded-sm bg-border">
                    <div className="h-full rounded-sm bg-primary" style={{ width: `${Math.round((src.count / (topSources[0]?.count || 1)) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : undefined
        }
      />

      <PageHeader
        title="Leads"
        summary={list.total}
        switcher={<ViewSwitch label="Lead layout" value={viewMode} onChange={setViewMode} options={[{ key: "table", label: "Table" }, { key: "board", label: "Board" }]} />}
        actions={
          <>
            {view === "followup" && (viewCounts.followup || 0) > 0 && (
              <Button size="sm" onClick={() => void startFollowUps()} title="Open the lead who has waited longest; Next takes you through the list one by one">
                <PhoneForwarded className="size-4 mr-1.5" />
                Start contacting
              </Button>
            )}
            {canImport && (
              <Button
                variant="outline"
                size="sm"
                className="hidden md:inline-flex"
                onClick={() => navigate("/leads/import")}
              >
                <Upload className="size-4 mr-1.5" />
                Import
              </Button>
            )}
            <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={() => setDupesOpen(true)}>
              Find duplicates
            </Button>
            <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={exportAll} disabled={exporting}>
              Export
            </Button>
            <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={loadLeads} disabled={loading}>
              <RefreshCw className={cn("size-4 mr-1.5", loading && "animate-spin")} />
              Refresh
            </Button>
            <Button size="sm" data-new-record onClick={() => navigate("/leads/new")}>
              <UserPlus className="size-4 mr-1.5" />
              New lead
            </Button>
          </>
        }
      />

      <StatusTabs
        label="Lead status"
        active={statusTab}
        onChange={(k) => {
          setStatusTab(k);
          setSelectedLeadIds(new Set());
        }}
        tabs={[
          { key: "all", label: "All", count: statusCounts.ALL },
          { key: "NEW", label: "New", count: statusCounts.NEW },
          { key: "CONTACTED", label: "Contacted", count: statusCounts.CONTACTED },
          { key: "FOLLOW_UP", label: "Follow-up", count: statusCounts.FOLLOW_UP },
          { key: "QUALIFIED", label: "Qualified", count: statusCounts.QUALIFIED },
          { key: "CONVERTED", label: "Converted", count: statusCounts.CONVERTED },
          { key: "DISQUALIFIED", label: "Disqualified", count: statusCounts.DISQUALIFIED },
        ]}
      />

      {campaignOpen && (
        <CampaignDialog
          open={campaignOpen}
          onOpenChange={setCampaignOpen}
          selectedIds={Array.from(selectedLeadIds)}
          matching={{ total: list.total, filters: campaignFilters() }}
          onCreated={(id) => { setSelectedLeadIds(new Set()); navigate(`/whatsapp/campaigns/${id}`); }}
        />
      )}

      <BulkBar count={selectedLeadIds.size} onClear={() => setSelectedLeadIds(new Set())}>
        {canAssign && (
          <BulkButton onClick={() => setBulkAssignOpen(true)}>
            <Share2 className="size-3.5" />
            Reassign
          </BulkButton>
        )}
        {hasPermission("lead.update") && <BulkButton onClick={() => setPriorityOpen(true)}>Change priority</BulkButton>}
        {hasPermission("lead.update") && <BulkButton onClick={() => setBulkEditOpen(true)}>Edit a field</BulkButton>}
        {hasPermission("lead.update") && selectedLeadIds.size === 2 && <BulkButton onClick={() => setMergeIds(Array.from(selectedLeadIds) as [string, string])}>Merge</BulkButton>}
        {hasPermission("activity.create") && (
          <BulkButton
            onClick={() => {
              setTaskForm({ title: "", due: new Date(Date.now() + 86400000).toISOString().slice(0, 16), type: "CALL" });
              setTaskOpen(true);
            }}
          >
            Add task
          </BulkButton>
        )}
        {hasPermission("whatsapp.campaign") && (
          <BulkButton onClick={() => setCampaignOpen(true)}>
            <WhatsAppIcon className="size-3.5" />
            WhatsApp campaign
          </BulkButton>
        )}
        <BulkButton onClick={exportSelected}>Export</BulkButton>
        {canDelete && <BulkButton onClick={() => setDeleteManyOpen(true)}>Delete</BulkButton>}
      </BulkBar>

      {/* Filter and Search Bar (no funnel status filter by design) */}
      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search by name, company, email, phone...",
          onEnter: loadLeads,
        }}
        fields={[
          { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(leadOptions.owners, leadOptions.total, user?.Id), defaultValue: "all" },
          { name: "priority", label: "Priority", icon: FilterPriorityIcon, type: "select", options: PRIORITY_OPTIONS, defaultValue: "all" },
          { name: "source", label: "Source", icon: FilterSourceIcon, type: "select", options: valueOptionsFromFacets(leadOptions.sources, "All sources", leadOptions.total, true), defaultValue: "all" },
          { name: "state", label: "State", icon: FilterStateIcon, type: "select", options: valueOptionsFromFacets(leadOptions.states, "All states", leadOptions.total), defaultValue: "all" },
          { name: "inactive", label: "Not contacted for", icon: FilterInactiveIcon, type: "select", options: INACTIVE_OPTIONS, defaultValue: "any" },
          { name: "created", label: "Created", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
        ]}
        values={leadFilters.values}
        onChange={(name, value) => leadFilters.set(name as keyof typeof LEAD_FILTER_DEFAULTS, value)}
        onReset={leadFilters.reset}
        quickKey="leads"
        fieldFilters={{ fields: leadFields || [], value: fieldFilters.values, onChange: fieldFilters.setAll, suggestUrl: "/api/Lead/FilterValues" }}
        sort={{ fields: leadFields || [], value: sorting.sort, onChange: sorting.setSort }}
        shown={list.total}
        total={list.total}
        noun="leads"
        columns={{ options: LEAD_COLUMNS, value: columns.visible, onChange: columns.setVisible }}
      />

      {viewMode === "board" && (
        <LeadsBoard
          canMove={hasPermission("lead.update")}
          onChanged={list.reload}
          query={{
            search: search.trim(),
            tab: view === "all" ? "" : view,
            owner: leadFilters.values.owner,
            priority: leadFilters.values.priority,
            source: leadFilters.values.source,
            state: leadFilters.values.state,
            inactive_days: inactiveParam(leadFilters.values.inactive),
            ...dateRangeParams(leadFilters.values.created, "created"),
            f: fieldFilters.param(leadFields),
            sort: sorting.sort,
          }}
        />
      )}

      {/* Leads Table */}
      <div className={cn("border border-border rounded-lg bg-card overflow-x-auto", viewMode === "board" && "hidden")}>
        {loading && leads.length === 0 ? (
          <TableSkeleton rows={7} columns={8} />
        ) : filteredLeads.length === 0 ? (
          <ListEmpty
            noun="leads" error={list.error} onRetry={list.reload}
            icon={UserPlus}
            compact={false}
            filtered={!!search.trim() || view !== "all" || statusTab !== "all" || leadFilters.activeCount > 0 || fieldFilters.activeCount > 0}
            onClear={() => {
              setSearch("");
              setView("all");
              setStatusTab("all");
              leadFilters.reset();
              fieldFilters.reset();
            }}
            createLabel="+ New lead"
            onCreate={hasPermission("lead.create") ? () => navigate("/leads/new") : undefined}
            firstUse="Leads arrive from your channels, or you can add one yourself."
          />
        ) : (
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow className="bg-panel hover:bg-panel border-b border-border">
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    checked={selectedLeadIds.size === filteredLeads.length && filteredLeads.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-border size-4 accent-primary cursor-pointer"
                  />
                </TableHead>
                <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Prospect & Contact
                </TableHead>
                <TableHead className={cn("text-[11px] font-semibold uppercase tracking-wide text-muted-foreground", colHidden("company"))}>
                  Company & Location
                </TableHead>
                <TableHead className={cn("text-[11px] font-semibold uppercase tracking-wide text-muted-foreground", colHidden("owner"))}>
                  Assigned User
                </TableHead>
                <TableHead className={cn("text-[11px] font-semibold uppercase tracking-wide text-muted-foreground", colHidden("need"))}>
                  Requirement / Source
                </TableHead>
                <TableHead className={cn("text-[11px] font-semibold uppercase tracking-wide text-muted-foreground", colHidden("priority"))}>
                  Priority
                </TableHead>
                <TableHead className={cn("text-[11px] font-semibold uppercase tracking-wide text-muted-foreground", colHidden("followup"))}>
                  Follow-up
                </TableHead>
                <TableHead className="text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground pr-4">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.map((l) => {
                const isSelected = selectedLeadIds.has(l.id);
                const isUnassigned = !l.owner_id;

                return (
                  <TableRow
                    key={l.id}
                    className={cn(
                      "group/row hover:bg-muted/30 transition-colors border-b border-border/60",
                      isSelected && "bg-primary/5 hover:bg-primary/10"
                    )}
                  >
                    {/* Checkbox */}
                    <TableCell className="w-10">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectLead(l.id)}
                        className="rounded border-border size-4 accent-primary cursor-pointer"
                      />
                    </TableCell>

                    {/* Person */}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="size-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0">
                          {l.first_name?.[0]?.toUpperCase() || "L"}
                        </div>
                        <div className="min-w-0">
                          <Link
                            to={`/leads/${l.id}${followUpLinks}`}
                            className="font-semibold text-sm text-foreground hover:text-primary transition-colors text-left flex items-center gap-1.5 hover:underline"
                          >
                            <span>{l.first_name} {l.last_name || ""}</span>
                            {l.job_title && (
                              <span className="text-[10px] text-muted-foreground font-normal bg-muted px-1.5 py-0.5 rounded">
                                {l.job_title}
                              </span>
                            )}
                            <PreviewButton entity="Lead" id={l.id} label={`${l.first_name} ${l.last_name || ""}`.trim()} />
                          </Link>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                            {l.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="size-3 text-muted-foreground" />
                                <span className="truncate max-w-[110px]">{l.email}</span>
                              </span>
                            )}
                            {(l.phone || l.mobile) && (
                              <span className="flex items-center gap-1">
                                <Phone className="size-3 text-muted-foreground" />
                                <span>{l.phone || l.mobile}</span>
                                <CallLink
                                  type="LEAD"
                                  id={l.id}
                                  number={l.mobile || l.phone}
                                  title="Call from the CRM"
                                  aria-label={`Call ${`${l.first_name} ${l.last_name || ""}`.trim()}`}
                                  className="inline-flex size-6 items-center justify-center rounded-md border border-border bg-card text-primary hover:bg-accent"
                                >
                                  <Phone className="size-3" />
                                </CallLink>
                                <WhatsAppButton
                                  phone={l.mobile || l.phone}
                                  name={`${l.first_name} ${l.last_name || ""}`.trim()}
                                  leadId={l.id}
                                />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>

                    {/* Company */}
                    <TableCell className={colHidden("company")}>
                      <div className="min-w-0">
                        <Link
                          to={`/leads/${l.id}${followUpLinks}`}
                          className="text-xs font-semibold text-foreground hover:text-primary hover:underline truncate block"
                        >
                          {l.company_name}
                        </Link>
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
                          {l.city && (
                            <span className="flex items-center gap-0.5">
                              <MapPin className="size-3" />
                              <span>{l.city}</span>
                            </span>
                          )}
                          {l.industry && <span>• {l.industry}</span>}
                        </div>
                      </div>
                    </TableCell>

                    {/* Assigned User */}
                    <TableCell className={colHidden("owner")}>
                      {isUnassigned ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-warning/10 text-warning border border-warning/20">
                            Unassigned
                          </span>
                          {canAssign && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openReassign(l)}
                              className="h-6 px-2 text-[10px] bg-primary text-primary-foreground hover:bg-primary/90"
                            >
                              Assign User
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2 max-w-[180px]">
                          <OwnerCell list="Lead" id={l.id} ownerId={l.owner_id} owner={l.owner} forLead unassignedLabel="Assigned" enabled={canAssign && l.lead_status !== "CONVERTED"} onSaved={(row) => list.patchRow(l.id, row)} onConflict={list.reload} />
                        </div>
                      )}
                    </TableCell>

                    {/* Requirement / Source */}
                    <TableCell className={cn("max-w-[200px]", colHidden("need"))}>
                      <div className="text-xs text-foreground truncate font-medium">
                        {l.product_interest || l.requirement_notes || "No details"}
                      </div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                        <Tag className="size-2.5" />
                        <span>Source: {l.source || "Direct"}</span>
                        {l.inquiry_count && l.inquiry_count > 1 ? (
                          <span className="inline-flex items-center text-[9px] bg-primary/15 text-primary font-semibold px-1 py-0.2 rounded" title={`${l.inquiry_count} inquiries received from this contact`}>
                            {l.inquiry_count}x
                          </span>
                        ) : null}
                      </div>
                    </TableCell>

                    {/* Priority & Score */}
                    <TableCell className={colHidden("priority")}>
                      <InlineCell
                        list="Lead" id={l.id} field="priority" kind="enum" value={l.priority}
                        enabled={canEditInline && l.lead_status !== "CONVERTED"}
                        options={[{ value: "HOT", label: "Hot" }, { value: "WARM", label: "Warm" }, { value: "COLD", label: "Cold" }]}
                        display={renderPriorityBadge(l.priority, l.score)}
                        onSaved={(row) => list.patchRow(l.id, row)} onConflict={list.reload}
                      />
                    </TableCell>

                    {/* Next Follow Up */}
                    <TableCell className={colHidden("followup")}>
                      <InlineCell
                        list="Lead" id={l.id} field="next_follow_up_at" kind="datetime" value={l.next_follow_up_at}
                        enabled={canEditInline && l.lead_status !== "CONVERTED"}
                        display={renderFollowUpPill(l.next_follow_up_at)}
                        onSaved={(row) => list.patchRow(l.id, row)} onConflict={list.reload}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right pr-4">
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Push to Sales Pipeline: only qualified leads convert (New → Contacted → Qualified → Converted) */}
                        {canConvert && l.lead_status === "QUALIFIED" && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setConvertLead(l);
                              setConvertForm({
                                create_deal: true,
                                deal_name: `${l.first_name} ${l.last_name || ""} - ${l.company_name}`.trim(),
                                deal_amount: Number(l.estimated_value || l.expected_budget || 10000),
                                expected_close_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
                              });
                            }}
                            className="h-7 gap-1 bg-sidebar px-2.5 text-[11px] font-semibold text-white hover:bg-sidebar/90"
                            title="Convert into an account, contact and deal"
                          >
                            <Briefcase className="size-3" />
                            <span>Convert</span>
                          </Button>
                        )}

                        {/* Edit */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/leads/${l.id}/edit`)}
                          title="Edit Lead"
                          className="size-7 text-muted-foreground hover:text-foreground"
                        >
                          <Edit className="size-3.5" />
                        </Button>

                        {/* Assign Lead */}
                        {canAssign && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openReassign(l)}
                            title="Assign to Sales Rep"
                            className="size-7 text-muted-foreground hover:text-foreground"
                          >
                            <Share2 className="size-3.5" />
                          </Button>
                        )}

                        {/* Delete */}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteLead(l)}
                            title="Delete Lead"
                            className="size-7 text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
      </div>

      <LeadMergeDialog
        open={!!mergeIds}
        ids={mergeIds}
        onClose={() => setMergeIds(null)}
        onMerged={() => {
          setSelectedLeadIds(new Set());
          list.reload();
        }}
      />
      <DuplicateGroupsDialog open={dupesOpen} onClose={() => setDupesOpen(false)} canMerge={hasPermission("lead.update")} onChanged={list.reload} />

      <BulkEditDialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} list="Lead" ids={Array.from(selectedLeadIds)} fields={LEAD_BULK_FIELDS} onDone={() => list.reload()} />

      {/* Bulk: priority */}
      <Dialog open={priorityOpen} onOpenChange={setPriorityOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Change priority</DialogTitle>
            <DialogDescription>Set the {selectedLeadIds.size} selected leads to one priority. Converted leads are skipped.</DialogDescription>
          </DialogHeader>
          <SearchableSelect value={bulkPriority} onChange={(e) => setBulkPriority(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
            <option value="HOT">Hot</option>
            <option value="WARM">Warm</option>
            <option value="COLD">Cold</option>
          </SearchableSelect>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriorityOpen(false)}>Cancel</Button>
            <Button disabled={actionLoading} onClick={submitBulkPriority}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk: add a task to each selected lead */}
      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add a task</DialogTitle>
            <DialogDescription>One task is created on each of the {selectedLeadIds.size} selected leads, for the lead's owner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="What needs doing, e.g. Call to qualify" value={taskForm.title} onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))} />
            <div className="flex gap-2">
              <SearchableSelect value={taskForm.type} onChange={(e) => setTaskForm((f) => ({ ...f, type: e.target.value }))} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                <option value="CALL">Call</option>
                <option value="EMAIL">Email</option>
                <option value="WHATSAPP">WhatsApp</option>
                <option value="FOLLOW_UP">Follow-up</option>
              </SearchableSelect>
              <Input type="datetime-local" value={taskForm.due} onChange={(e) => setTaskForm((f) => ({ ...f, due: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskOpen(false)}>Cancel</Button>
            <Button disabled={actionLoading || !taskForm.title.trim() || !taskForm.due} onClick={submitBulkTask}>Create tasks</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk: delete */}
      <Dialog open={deleteManyOpen} onOpenChange={setDeleteManyOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete {selectedLeadIds.size} {selectedLeadIds.size === 1 ? "lead" : "leads"}?</DialogTitle>
            <DialogDescription>This cannot be undone. Converted leads are kept for the audit trail.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteManyOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={actionLoading} onClick={submitBulkDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* STATUS CHANGE / DISQUALIFY DIALOG */}
      <Dialog open={!!statusLead} onOpenChange={(open) => !open && setStatusLead(null)}>
        <DialogContent className="sm:max-w-lg md:max-w-xl bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
              <UserCheck className="size-5 text-primary" />
              <span>Update Lifecycle Status</span>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              Transition lead "{statusLead?.company_name}" across sales funnel stages.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleStatusSubmit} className="space-y-4 text-xs sm:text-sm">
            <div>
              <Label className="text-xs sm:text-sm font-semibold">Target Stage</Label>
              <SearchableSelect
                value={targetStatus}
                onChange={(e) => setTargetStatus(e.target.value as LeadStatus)}
                className="w-full mt-1.5 h-9 sm:h-10 rounded-md border border-border bg-background px-3 sm:px-3.5 text-xs sm:text-sm font-medium"
              >
                <option value="NEW">New</option>
                <option value="CONTACTED">Contacted</option>
                <option value="FOLLOW_UP">Follow-up</option>
                <option value="QUALIFIED">Qualified</option>
                <option value="DISQUALIFIED">Disqualified</option>
              </SearchableSelect>
            </div>

            {targetStatus === "FOLLOW_UP" && (
              <div>
                <Label className="text-xs sm:text-sm font-semibold">Follow up on (optional)</Label>
                <Input type="datetime-local" value={followUpAt} onChange={(e) => setFollowUpAt(e.target.value)} className="mt-1.5" />
                <p className="mt-1 text-[11.5px] text-muted-foreground">A task is added for this date; left empty it is tomorrow at 10:00.</p>
              </div>
            )}

            {targetStatus === "DISQUALIFIED" && (
              <div className="space-y-3 bg-destructive/5 p-3.5 rounded-lg border border-destructive/20">
                <div>
                  <Label className="text-xs sm:text-sm font-semibold text-destructive">
                    Disqualification Reason *
                  </Label>
                  <SearchableSelect
                    value={disqualifyReason}
                    onChange={(e) => setDisqualifyReason(e.target.value)}
                    className="w-full mt-1.5 h-9 sm:h-10 rounded-md border border-border bg-background px-3 sm:px-3.5 text-xs sm:text-sm"
                    required
                  >
                    <option value="">-- Select Disqualification Reason --</option>
                    {masterDisqualifyReasons.map((r) => (
                      <option key={r.code} value={r.name}>
                        {r.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>

                <div>
                  <Label className="text-xs sm:text-sm font-semibold text-destructive">
                    Reason Notes
                  </Label>
                  <textarea
                    rows={2}
                    placeholder="Specific explanation on why this lead is not viable..."
                    value={disqualifyNotes}
                    onChange={(e) => setDisqualifyNotes(e.target.value)}
                    className="w-full mt-1.5 rounded-md border border-border bg-background p-2.5 text-xs sm:text-sm"
                  />
                </div>
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setStatusLead(null)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={actionLoading}>
                {actionLoading ? "Updating..." : "Confirm Transition"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CONVERT LEAD DIALOG */}
      <Dialog open={!!convertLead} onOpenChange={(open) => !open && setConvertLead(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold flex items-center gap-2 text-success">
              <ArrowRightCircle className="size-5" />
              <span>Convert Lead to Opportunity</span>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              Qualify prospect into permanent CRM records.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleConvertSubmit} className="space-y-3.5 text-xs sm:text-sm">
            <div className="p-3 bg-muted/40 rounded-lg space-y-2 border border-border text-xs sm:text-sm">
              <div className="flex items-center gap-2">
                <Building className="size-4 text-primary" />
                <span>Account created: <strong>{convertLead?.company_name}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <User className="size-4 text-primary" />
                <span>Contact created: <strong>{convertLead?.first_name} {convertLead?.last_name || ""}</strong></span>
              </div>
            </div>

            <div className="pt-1 space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Deal Opportunity Name</Label>
              <Input
                required
                value={convertForm.deal_name}
                onChange={(e) => setConvertForm({ ...convertForm, deal_name: e.target.value })}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-semibold">Deal Amount (₹)</Label>
                <Input
                  type="number"
                  value={convertForm.deal_amount}
                  onChange={(e) => setConvertForm({ ...convertForm, deal_amount: Number(e.target.value) })}
                  className="mt-1"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-semibold">Expected Close Date</Label>
                <Input
                  type="date"
                  value={convertForm.expected_close_date}
                  onChange={(e) => setConvertForm({ ...convertForm, expected_close_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" size="sm" onClick={() => setConvertLead(null)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-success hover:bg-success/90 text-white" disabled={actionLoading}>
                {actionLoading ? "Converting..." : "Complete Conversion"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* BULK ASSIGN DIALOG */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent className="sm:max-w-lg md:max-w-xl bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
              <Share2 className="size-5 text-primary" />
              <span>Bulk Reassign Leads</span>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              Transfer {selectedLeadIds.size} selected leads to a designated sales representative.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleBulkAssignSubmit} className="space-y-3.5 text-xs sm:text-sm">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Select Destination Owner *</Label>
              <SearchableSelect
                required
                value={bulkTargetOwner}
                onChange={(e) => setBulkTargetOwner(e.target.value)}
                className="w-full mt-1 h-9 sm:h-10 rounded-md border border-border bg-background px-3 sm:px-3.5 text-xs sm:text-sm font-medium"
              >
                <option value="">-- Choose Sales Representative --</option>
                {assignableReps.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.first_name} {r.last_name || ""} — {r.user_role?.name || "Representative"} ({r.email})
                  </option>
                ))}
              </SearchableSelect>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Reassignment Reason / Note</Label>
              <Input
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="e.g. Territory realignment, rep leave transfer"
                className="mt-1"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setBulkAssignOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={actionLoading}>
                {actionLoading ? "Reassigning..." : `Reassign ${selectedLeadIds.size} Leads`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* SINGLE LEAD REASSIGN / ASSIGN DIALOG */}
      <Dialog open={!!reassignLead} onOpenChange={(open) => !open && setReassignLead(null)}>
        <DialogContent className="sm:max-w-lg md:max-w-xl bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
              <Share2 className="size-5 text-primary" />
              <span>{reassignLead?.owner ? "Reassign Lead to User" : "Assign Lead to User"}</span>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              {reassignLead?.first_name} {reassignLead?.last_name || ""} ({reassignLead?.company_name}) is
              currently{" "}
              {reassignLead?.owner ? (
                <>
                  assigned to <strong className="text-foreground">{reassignLead.owner.first_name} {reassignLead.owner.last_name || ""}</strong>.
                </>
              ) : (
                <strong className="text-warning font-semibold">unassigned (in pool)</strong>
              )}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleReassignSubmit} className="space-y-3.5 text-xs sm:text-sm">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Select Sales Representative *</Label>
              <SearchableSelect
                required
                value={reassignOwner}
                onChange={(e) => setReassignOwner(e.target.value)}
                className="w-full mt-1 h-9 sm:h-10 rounded-md border border-border bg-background px-3 sm:px-3.5 text-xs sm:text-sm font-medium"
              >
                <option value="">-- Choose Sales Representative --</option>
                {assignableReps
                  .filter((r) => r.id !== reassignLead?.owner_id)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.first_name} {r.last_name || ""} — {r.user_role?.name || "Representative"} ({r.email})
                    </option>
                  ))}
              </SearchableSelect>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Assignment Reason / Note</Label>
              <Input
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                placeholder="e.g. Lead allocation, rep follow-up assignment"
                className="mt-1"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setReassignLead(null)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={actionLoading} className="bg-primary hover:bg-primary/90">
                {actionLoading ? "Assigning..." : reassignLead?.owner ? "Confirm Reassignment" : "Confirm Assignment"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV IMPORT DIALOG */}
      {/* VIEW DETAILS / AUDIT DRAWER */}
      <Dialog open={!!viewLead} onOpenChange={(open) => !open && setViewLead(null)}>
        <DialogContent className="sm:max-w-3xl md:max-w-4xl bg-card border-border text-foreground max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg sm:text-xl font-bold flex items-center gap-2">
                <span>{viewLead?.company_name}</span>
                {renderPriorityBadge(viewLead?.priority, viewLead?.score)}
              </DialogTitle>
            </div>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              Contact: {viewLead?.first_name} {viewLead?.last_name || ""} • Designation: {viewLead?.job_title || "N/A"}
            </DialogDescription>
          </DialogHeader>

          {viewDetailsLoading ? (
            <div className="p-8 text-center text-xs sm:text-sm text-muted-foreground">Loading audit dossier...</div>
          ) : (
            <div className="space-y-4 pt-1 text-xs sm:text-sm">
              {/* Contact & Commercial Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="p-3.5 bg-muted/30 rounded-lg border border-border space-y-1.5">
                  <div className="font-semibold text-foreground">Contact Channels</div>
                  <div>Email: {viewLead?.email || "—"}</div>
                  <div>Phone: {viewLead?.phone || "—"}</div>
                  <div>Mobile: {viewLead?.mobile || "—"}</div>
                  {viewLead && (viewLead.mobile || viewLead.phone) && (
                    <WhatsAppButton
                      variant="label"
                      phone={viewLead.mobile || viewLead.phone}
                      name={`${viewLead.first_name} ${viewLead.last_name || ""}`.trim()}
                      leadId={viewLead.id}
                    />
                  )}
                  <div>City/State: {[viewLead?.city, viewLead?.state].filter(Boolean).join(", ") || "—"}</div>
                </div>

                <div className="p-3.5 bg-muted/30 rounded-lg border border-border space-y-1.5">
                  <div className="font-semibold text-foreground">Commercial Scope</div>
                  <div>Product Interest: {viewLead?.product_interest || "—"}</div>
                  <div>Budget: {viewLead?.expected_budget ? formatINR(viewLead.expected_budget) : "—"}</div>
                  <div>Timeline: {viewLead?.expected_timeline || "—"}</div>
                  <div>Source: {viewLead?.source || "—"}</div>
                </div>
              </div>

              {viewLead?.requirement_notes && (
                <div className="p-3.5 bg-muted/30 rounded-lg border border-border">
                  <div className="font-semibold text-foreground mb-1">Requirement Brief</div>
                  <p className="text-muted-foreground whitespace-pre-wrap">{viewLead.requirement_notes}</p>
                </div>
              )}

              {/* Tasks & Follow-up Reminders */}
              {viewLead && (
                <LeadTasksSection
                  leadId={viewLead.id}
                  leadName={[viewLead.first_name, viewLead.last_name].filter(Boolean).join(" ") || viewLead.company_name}
                  onFollowUpChanged={loadLeads}
                />
              )}

              {/* Assignment Audit Trail */}
              <div>
                <div className="font-semibold text-sm sm:text-base text-foreground mb-2 flex items-center gap-1.5">
                  <History className="size-4 text-primary" />
                  <span>Assignment & Lifecycle History</span>
                </div>

                {!viewLead?.assignment_history || viewLead.assignment_history.length === 0 ? (
                  <div className="p-3 bg-muted/20 rounded border border-border text-muted-foreground italic">
                    Original assignment to current owner.
                  </div>
                ) : (
                  <div className="space-y-2 border-l-2 border-border ml-2 pl-3">
                    {viewLead.assignment_history.map((hist) => (
                      <div key={hist.id} className="relative pb-2">
                        <div className="absolute -left-[19px] top-1 size-2 rounded-full bg-primary" />
                        <div className="font-medium text-foreground">
                          {hist.reason.replace(/_/g, " ")}: Transferred to{" "}
                          <strong>
                            {hist.to_user ? `${hist.to_user.first_name} ${hist.to_user.last_name || ""}` : "Unassigned Pool"}
                          </strong>
                        </div>
                        <div className="text-[11px] sm:text-xs text-muted-foreground">
                          By {hist.assigned_by ? `${hist.assigned_by.first_name}` : "System"} on{" "}
                          {new Date(hist.created_on).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="pt-2 border-t border-border">
            <Button size="sm" variant="outline" onClick={() => setViewLead(null)}>
              Close Dossier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={!!deleteLead} onOpenChange={(open) => !open && setDeleteLead(null)}>
        <DialogContent className="sm:max-w-md bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-destructive">
              Delete Lead Record?
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Are you sure you want to delete the lead for "{deleteLead?.company_name}"? This action can only be taken by an administrator.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" size="sm" onClick={() => setDeleteLead(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={actionLoading}
            >
              {actionLoading ? "Deleting..." : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LeadsPage;
